/**
 * voice-metrics — «Голосовые метрики»: темп речи, паузы, вариативность тона,
 * стабильность голоса. Зеркалит VoiceMetricsSection.tsx один в один — включая
 * гейт правдоподобия WPM (H-1); лестница интерпретации пауз больше не
 * копируется, а импортируется из `@/lib/records/pause-rhythm`, общего с
 * экраном.
 *
 * `speechRate` печатается только при `wpm` в [40, 260] — вне диапазона это
 * деградировавший вход (сломанный VAD/фолбэк на артикуляционную оценку), а
 * не реальный темп; печатается `voiceMetrics.unavailable`. Границы
 * ПРОДУБЛИРОВАНЫ из VoiceMetricsSection.tsx намеренно: это локальные
 * константы модуля экрана (не экспортированы), а печать не импортирует
 * React-компоненты. Если границы там изменятся, эта копия должна измениться
 * вместе — единственное место истины по СМЫСЛУ значений — комментарий H-1 в
 * VoiceMetricsSection.tsx, не эта копия.
 *
 * Строки `description`/`acousticSummary` из ответа акустического сервиса
 * (`voiceMetricsSchema`) — захардкожены по-русски на стороне сервиса и НЕ
 * печатаются никогда, ни в каком поле. Печатаются только числа и
 * локализованные категориальные уровни: бейдж — через `voiceMetrics.terms.
 * <value>` (та же строка для ЛЮБОГО уровня — экран красит все бейджи
 * voiceMetrics нейтральным `Tag variant="neutral"`, без scoreBand), пояснение
 * — через `voiceMetrics.descriptions.<path>`.
 *
 * `loudness` — есть в `voiceMetricsSchema`, но VoiceMetricsSection.tsx его не
 * показывает вовсе (тот же случай, что operatorErrors в speech.ts) — здесь
 * тоже не печатается.
 *
 * Эмоция ПОЛНОСТЬЮ отсутствует в `VoiceMetrics` — это не гейт этого модуля,
 * а инвариант типа (`packages/api-types/src/schemas/analyses.ts`): клиент
 * акустического сервиса срезает поле `emotion` на границе до сохранения
 * (EU AI Act, ст. 5(1)(f)). Печатать нечего — здесь нет ни строки кода,
 * которая могла бы это нарушить.
 */
import type { TFunction } from 'i18next';
import { formatPauseValue, resolvePauseRhythm } from '@/lib/records/pause-rhythm';
import { card, kvRow, tag } from '../primitives';
import { escapeHtml } from '../escape';
import type { SectionFn, TFn } from '../types';

// H-1, зеркало VoiceMetricsSection.tsx: WPM вне диапазона — деградировавший
// вход, не реальный темп речи.
const PLAUSIBLE_WPM_MIN = 40;
const PLAUSIBLE_WPM_MAX = 260;

function term(value: string, t: TFn): string {
  return t(`records.analysisPanel.voiceMetrics.terms.${value}`, value);
}

function desc(path: string, t: TFn): string {
  return t(`records.analysisPanel.voiceMetrics.descriptions.${path}`, '');
}

/**
 * Пояснение печатается той же муted-строкой в конце `<dd>`, что и топ-слова
 * паразитов/стоп-слов в speech.ts (одна секция — один визуальный язык), а не
 * отдельным `<p>` под строкой: у `<p>` нет собственного CSS-правила вне
 * `.blk`/`.box`/`.rec`, а заводить под четыре однострочных метрики ещё и
 * `.blk`-обёртку ради одного абзаца — лишняя вложенность без выигрыша.
 */
function metricRow(labelText: string, value: string, description: string): string {
  const detail = description
    ? ` <span style="color:var(--fg3);font-size:10px">— ${escapeHtml(description)}</span>`
    : '';
  return kvRow(labelText, value + detail);
}

export const renderVoiceMetrics: SectionFn = (input, t) => {
  const voiceMetrics = input.analysis.voiceMetrics;
  if (!voiceMetrics) return '';

  const { speechRate, pauses, pitch, voiceQuality } = voiceMetrics;

  const plausibleSpeechRate =
    speechRate != null &&
    speechRate.wpm >= PLAUSIBLE_WPM_MIN &&
    speechRate.wpm <= PLAUSIBLE_WPM_MAX
      ? speechRate
      : null;

  const wpmUnit = escapeHtml(t('records.analysisPanel.voiceMetrics.wpm'));
  const speechRateHtml = plausibleSpeechRate
    ? metricRow(
        t('records.analysisPanel.voiceMetrics.speakingRate'),
        `${plausibleSpeechRate.wpm} ${wpmUnit} ${tag(term(plausibleSpeechRate.level, t), 'muted')}`,
        desc(`speechRate.${plausibleSpeechRate.level}`, t)
      )
    : metricRow(
        t('records.analysisPanel.voiceMetrics.speakingRate'),
        escapeHtml(t('records.analysisPanel.voiceMetrics.unavailable')),
        ''
      );

  // Лестница ритма пауз и строка деталей — не копия экрана, а тот же модуль
  // (@/lib/records/pause-rhythm): раньше здесь лежал побайтово идентичный
  // дубль, и правка порогов в одном файле молча расходила печать с панелью,
  // которую клиент только что прочитал.
  const pauseKey = resolvePauseRhythm(pauses);
  // Значение строки пауз — из того же модуля, что и лестница: при
  // деградировавшем VAD сервис обнуляет все цифры пауз, и печатать их как
  // измерения означало печатать их ровно над собственной подписью
  // «недостаточно данных».
  const pausesHtml = metricRow(
    t('records.analysisPanel.voiceMetrics.pauses'),
    escapeHtml(formatPauseValue(pauses, t as unknown as TFunction)),
    desc(`pauses.${pauseKey}`, t)
  );

  const hzUnit = escapeHtml(t('records.analysisPanel.voiceMetrics.hz'));
  const pitchHtml = metricRow(
    t('records.analysisPanel.voiceMetrics.pitch'),
    `${pitch.rangeHz} ${hzUnit} ${tag(term(pitch.variability, t), 'muted')}`,
    desc(`pitch.${pitch.variability}`, t)
  );

  // Пояснение стабильности — как на экране, к описанию стабильности
  // добавляется фраза о придыхании, только если сервис его зафиксировал.
  const voiceQualityDesc = [
    desc(`voiceStability.${voiceQuality.stability}`, t),
    voiceQuality.breathiness === 'breathy' ? desc('breathiness.breathy', t) : '',
  ]
    .filter(Boolean)
    .join(' · ');
  const voiceStabilityHtml = metricRow(
    t('records.analysisPanel.voiceMetrics.voiceStability'),
    tag(term(voiceQuality.stability, t), 'muted'),
    voiceQualityDesc
  );

  return card({
    title: t('records.analysisPanel.voiceMetrics.title'),
    body: speechRateHtml + pausesHtml + pitchHtml + voiceStabilityHtml,
  });
};
